import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { MapController } from './MapController.js';
import { useMapLayers } from './useMapLayers.js';
import './map.css';

const MapControllerContext = createContext<MapController | null>(null);

/** Access the imperative MapController from chrome components. */
export function useMapController(): MapController | null {
  return useContext(MapControllerContext);
}

interface MapShellProps {
  children?: ReactNode;
}

/**
 * Owns the Leaflet instance for its lifetime. React renders `children` (chrome,
 * controls, sheets) around the map div — never inside the map pane (spec 01).
 */
export function MapShell({ children }: MapShellProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [controller, setController] = useState<MapController | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const c = new MapController(containerRef.current);
    setController(c);
    return () => {
      c.destroy();
      setController(null);
    };
  }, []);

  // Every map follows the app-wide chart + overlay choices (spec 07).
  useMapLayers(controller);

  return (
    <MapControllerContext.Provider value={controller}>
      <div className="map-shell">
        <div ref={containerRef} className="map-pane" aria-label="Sjøkart" />
        {children}
      </div>
    </MapControllerContext.Provider>
  );
}
